/**
 * Text metrics of the Segoe UI Regular the PDF embeds (public/fonts/SegoeUI-Regular.ttf),
 * so the editor can tell how many lines a footnote takes without rendering a PDF.
 *
 * The advance widths were read from that file with fontkit — the library react-pdf lays
 * text out with — and are held against it in squadSegoeUiMetrics.test.ts. Kerning is left
 * out: on SQUAD's page-3 note it narrows the line by 0.2 %, so leaving it out errs on
 * the side of one line too many, never one too few.
 */

const UNITS_PER_EM = 2048;

/**
 * Advance width in font units, grouped by width: Latin-1 plus German typography. Soft
 * hyphen and zero-width space take no room as laid out (react-pdf drops the one, the font
 * gives the other none); the narrow spaces keep their real width.
 */
const ADVANCE_GROUPS: Array<[number, string]> = [
  [0, '\u00ad\u200b'],
  [280, '\u202f'],
  [410, '\u2009'],
  [420, '¸'],
  [444, ',.:;·'],
  [469, '‘’‚′'],
  [471, "'"],
  [490, '|¦'],
  [496, 'ijlìíîï'],
  [545, 'IÌÍÎÏ'],
  [549, '`'],
  [561, '  '],
  [578, '´'],
  [582, '!¡'],
  [618, '()[]{}'],
  [641, 'f'],
  [694, 't'],
  [712, 'r'],
  [719, '¹⁴'],
  [731, 'J'],
  [750, '²³⁰⁵⁶⁷⁸⁹'],
  [768, '†‡'],
  [772, '°“”„″'],
  [776, '\\'],
  [798, '/'],
  [803, '"ª'],
  [819, '-'],
  [832, '•'],
  [848, '¨'],
  [850, '_¯'],
  [854, '*'],
  [869, 's'],
  [882, 'º'],
  [918, '?§¿'],
  [926, 'z'],
  [938, '¶'],
  [940, 'x'],
  [946, 'cç'],
  [964, 'L'],
  [981, 'v'],
  [991, 'yýÿ'],
  [1000, 'F'],
  [1018, 'k'],
  [1024, '–'],
  [1036, 'E«»ÈÉÊË'],
  [1042, 'aàáâãäå'],
  [1071, 'eèéêë'],
  [1073, 'T'],
  [1088, 'S'],
  [1104, '$0123456789¢£¥€'],
  [1114, 'ß'],
  [1132, 'YÝ'],
  [1138, '¤'],
  [1145, 'ð'],
  [1147, 'PÞ'],
  [1159, 'hnuñùúûü'],
  [1168, 'Z'],
  [1174, 'B'],
  [1182, 'µ'],
  [1188, 'K'],
  [1200, 'oòóôõöø'],
  [1204, 'bpþ'],
  [1206, 'dgq'],
  [1208, 'X'],
  [1210, '#'],
  [1225, 'R'],
  [1268, 'CÇ'],
  [1272, 'V'],
  [1321, 'AÀÁÂÃÄÅ'],
  [1401, '+<=>^~¬±×÷−'],
  [1405, 'G'],
  [1407, 'UÙÚÛÜ'],
  [1436, 'DÐ'],
  [1454, 'H'],
  [1480, 'w'],
  [1501, '…'],
  [1532, 'NÑ'],
  [1544, 'OQÒÓÔÕÖØ'],
  [1583, '™'],
  [1639, '&'],
  [1676, '%'],
  [1704, 'æ'],
  [1762, 'Æ'],
  [1764, 'm'],
  [1823, '©®'],
  [1839, 'M'],
  [1856, '¼'],
  [1906, '½'],
  [1913, 'W'],
  [1950, '¾'],
  [1956, '@'],
  [2048, '—'],
  [2478, '‰'],
];

const ADVANCE = new Map<string, number>();
ADVANCE_GROUPS.forEach(([width, chars]) => {
  for (const ch of chars) ADVANCE.set(ch, width);
});

/** Anything outside the table counts as wide as "m", so an estimate errs towards more lines. */
const FALLBACK_ADVANCE = 1764;

const SPACE_ADVANCE = 561;

/** Segoe UI line box per pt of font size: hhea ascender 2210 + descender 514, no line gap. */
export const SEGOE_UI_LINE_HEIGHT = (2210 + 514) / UNITS_PER_EM;

/** Unkerned width of `text` in pt, set in Segoe UI Regular at `fontSizePt`. */
export function segoeUiTextWidth(text: string, fontSizePt: number): number {
  let units = 0;
  for (const ch of text) units += ADVANCE.get(ch) ?? FALLBACK_ADVANCE;
  return (units * fontSizePt) / UNITS_PER_EM;
}

/**
 * Lines one paragraph takes when react-pdf wraps it at spaces into `widthPt`.
 *
 * Greedy, at natural word spacing and without hyphenation. react-pdf can do better on
 * both counts (it lets spaces shrink by a third and splits German words), so this never
 * comes out below the real count — at worst a line above it.
 */
function paragraphLineCount(paragraph: string, widthPt: number, fontSizePt: number): number {
  const space = (SPACE_ADVANCE * fontSizePt) / UNITS_PER_EM;
  let lines = 1;
  let lineWidth = 0;
  paragraph.split(' ').forEach((word, i) => {
    const wordWidth = segoeUiTextWidth(word, fontSizePt);
    const extended = i === 0 ? wordWidth : lineWidth + space + wordWidth;
    if (extended <= widthPt || i === 0) {
      lineWidth = extended;
    } else if (word !== '') {
      // A space at the break is dropped; a doubled one (empty word) never starts a line
      lines += 1;
      lineWidth = wordWidth;
    }
    // A word wider than the column is cut into pieces (germanSafeWrapCallback)
    while (lineWidth > widthPt) {
      lines += 1;
      lineWidth -= widthPt;
    }
  });
  return lines;
}

/**
 * Lines a note takes in the PDF as FootnoteLines sets it: one paragraph per typed line
 * (an empty one still takes a line), each wrapped into `widthPt`. 0 when it prints nothing.
 */
export function squadNoteLineCount(text: unknown, widthPt: number, fontSizePt: number): number {
  const raw = typeof text === 'string' ? text : '';
  if (!raw.trim()) return 0;
  return raw
    .split(/\r?\n/)
    .reduce((sum, paragraph) => sum + paragraphLineCount(paragraph, widthPt, fontSizePt), 0);
}
